import {
    applyMiddleware,
    combineReducers,
    createStore,
    Dispatch as ReduxDispatch,
    MiddlewareAPI as ReduxMiddlewareAPI,
    Store as ReduxStore,
} from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import localForage from 'localforage';
import { Persistor } from 'redux-persist/es/types';
import { createMigrate, persistReducer, persistStore } from 'redux-persist';

import { Action } from './action';
import { migrations } from './migrations';

import { appReducer } from './App/reducer';
import { authReducer } from './Auth/reducer';
import { errorReducer } from './Error/reducer';
import { filterReducer } from './Filter/reducer';
import { labelReducer } from './Label/reducer';
import { modificationLoanFormReducer } from './ModificationLoanform/reducer';
import { modalReducer } from './Modal/reducer';
import { pdfReducer } from './Pdf/reducer';
import { redirectReducer } from './Redirect/reducer';
import { periodReducer } from './Period/reducer';
import { propertiesReducer } from './Properties/reducer';
import { scopeReducer } from './Scope/reducer';

import { AppState } from './App/state';
import { AuthState } from './Auth/state';
import { ErrorState } from './Error/state';
import { FilterState } from './Filter/state';
import { LabelState } from './Label/state';
import { ModificationLoanFormState } from './ModificationLoanform/state';
import { ModalState } from './Modal/state';
import { PdfState } from './Pdf/state';
import { RedirectState } from './Redirect/state';
import { PeriodState } from './Period/state';
import { PropertiesState } from './Properties/state';
import { ScopeState } from './Scope/state';

export interface RootState {
    app: AppState;
    auth: AuthState;
    error: ErrorState;
    filter: FilterState;
    label: LabelState;
    loanForm: ModificationLoanFormState;
    modal: ModalState;
    pdf: PdfState;
    redirect: RedirectState;
    period: PeriodState;
    properties: PropertiesState;
    scopes: ScopeState;
}

export type Store = ReduxStore<RootState, Action>;
export type Dispatch = ReduxDispatch<Action>;
export type MiddlewareApi = ReduxMiddlewareAPI<Dispatch, RootState>;
export type Middleware = (api: MiddlewareApi) => (next: Dispatch) => (action: Action) => void;

export const rootReducer = combineReducers<RootState, Action>({
    app: appReducer,
    auth: authReducer,
    error: errorReducer,
    filter: filterReducer,
    label: labelReducer,
    loanForm: modificationLoanFormReducer,
    modal: modalReducer,
    pdf: pdfReducer,
    redirect: redirectReducer,
    period: periodReducer,
    properties: propertiesReducer,
    scopes: scopeReducer,
});

localForage.config({
    driver: [localForage.INDEXEDDB, localForage.WEBSQL, localForage.LOCALSTORAGE],
    name: 'findata',
    storeName: 'state',
});

const persistConfig = {
    key: 'root',
    version: 9,
    storage: localForage,
    whitelist: ['app', 'auth', 'filter', 'label', 'loanForm', 'period'],
    // TODO remove this ts-ignore lines when the following issue is fixed: https://github.com/rt2zz/redux-persist/issues/1065
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    migrate: createMigrate(migrations, { debug: process.env.NODE_ENV === 'development' }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export interface StoreConfiguration {
    persistor: Persistor;
    store: Store;
}

export class StoreConfigurator {
    public constructor(private middleware: Middleware[]) {}

    public initializeStore(previousState?: RootState): StoreConfiguration {
        const middlewareEnhancer = applyMiddleware(...this.middleware);
        const composedEnhancers = composeWithDevTools(middlewareEnhancer);

        const store = createStore(persistedReducer, previousState, composedEnhancers);
        return { store, persistor: persistStore(store) };
    }
}
